import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Star, MapPin, Heart, Info } from 'lucide-react';
import { ProviderSearchResult } from '@/hooks/useProviderSearch';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

interface ProviderCardProps {
  provider: ProviderSearchResult;
  isFavorite?: boolean;
  onFavoriteToggle?: () => void;
}

export function ProviderCard({ provider, isFavorite = false, onFavoriteToggle }: ProviderCardProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const isSponsored = provider.plan_slug === 'premium';

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!user) {
      toast.error('Debes iniciar sesión para guardar favoritos');
      navigate('/login');
      return;
    }

    setIsTogglingFavorite(true);

    try {
      if (isFavorite) {
        // Eliminar de favoritos
        const { error } = await supabase
          .from('provider_favorites')
          .delete()
          .eq('user_id', user.id)
          .eq('provider_id', provider.id);

        if (error) throw error;
        toast.success('Eliminado de favoritos');
      } else {
        // Agregar a favoritos
        const { error } = await supabase
          .from('provider_favorites')
          .insert({
            user_id: user.id,
            provider_id: provider.id,
          });

        if (error) throw error;
        toast.success('Agregado a favoritos');
      }

      onFavoriteToggle?.();
    } catch (error) {
      logger.error('Error toggling favorite:', error);
      toast.error('Error al actualizar favoritos');
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  return (
    <div
      className="group relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer border border-neutral-200 hover:border-neutral-300"
      onClick={() => navigate(`/provider/${provider.id}`)}
    >
      {/* Badge Patrocinado estilo Booksy (top-left) */}
      {isSponsored && (
        <div className="absolute top-3 left-3 z-10">
          <Badge className="bg-luxury-gold/95 hover:bg-luxury-gold text-black font-medium shadow-md flex items-center gap-1 px-2 py-1">
            Patrocinado
            <Info className="w-3 h-3" />
          </Badge>
        </div>
      )}

      {/* Botón de Favoritos estilo Booksy (top-right en contenido) */}
      <button
        onClick={handleFavoriteClick}
        disabled={isTogglingFavorite}
        className="absolute top-3 right-3 z-10 bg-white hover:bg-neutral-50 rounded-full p-2 shadow-md transition-all duration-200 hover:scale-110 disabled:opacity-50"
      >
        <Heart
          className={`w-5 h-5 transition-colors ${
            isFavorite
              ? 'fill-red-500 text-red-500'
              : 'text-neutral-600 hover:text-red-500'
          }`}
        />
      </button>

      {/* Imagen Grande estilo Booksy - Prioridad: Portfolio → Avatar → Placeholder */}
      <div className="relative h-48 bg-gradient-to-br from-neutral-100 to-neutral-50 overflow-hidden">
        {provider.featured_image || provider.avatar_url ? (
          <img
            src={provider.featured_image || provider.avatar_url}
            alt={provider.business_name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-gradient-to-br from-neutral-100 to-neutral-200">
            <span className="text-7xl font-bold text-neutral-300">
              {provider.business_name.charAt(0).toUpperCase()}
            </span>
          </div>
        )}

        {/* Badge de Rating estilo Booksy (esquina superior derecha sobre imagen) */}
        {provider.rating_count > 0 && (
          <div className="absolute top-3 right-3">
            <div className="bg-[#10b981] text-white px-3 py-1.5 rounded-lg shadow-lg flex items-center gap-1.5">
              <Star className="w-4 h-4 fill-white" />
              <span className="font-bold text-sm">{provider.avg_rating.toFixed(1)}</span>
              <span className="text-xs opacity-90">({provider.rating_count})</span>
            </div>
          </div>
        )}
      </div>

      {/* Contenido estilo Booksy */}
      <CardContent className="p-4 space-y-2">
        {/* Nombre del negocio - Bold estilo Booksy */}
        <h3 className="text-lg font-bold text-neutral-900 line-clamp-1 group-hover:text-[#B8860B] transition-colors">
          {provider.business_name}
        </h3>

        {/* Dirección completa estilo Booksy */}
        <div className="flex items-start text-neutral-600">
          <MapPin className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-neutral-400" />
          <span className="text-sm leading-tight line-clamp-2">
            {provider.address ? (
              <>
                {provider.address}
                {provider.city && `, ${provider.city}`}
                {provider.province && `, ${provider.province}`}
              </>
            ) : (
              `${provider.city}${provider.province ? `, ${provider.province}` : ''}`
            )}
          </span>
        </div>

        {/* Categorías compactas */}
        {provider.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {provider.categories.slice(0, 2).map((category) => (
              <Badge
                key={category}
                variant="secondary"
                className="text-xs bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
              >
                {category}
              </Badge>
            ))}
            {provider.categories.length > 2 && (
              <Badge variant="secondary" className="text-xs bg-neutral-100 text-neutral-600">
                +{provider.categories.length - 2}
              </Badge>
            )}
          </div>
        )}

        {/* Precio desde (compacto) */}
        <div className="pt-2 border-t border-neutral-100">
          <div className="flex items-center justify-between">
            <div className="flex items-baseline gap-1">
              <span className="text-xs text-neutral-500">Desde</span>
              <span className="text-xl font-bold text-neutral-900">
                {provider.min_price.toFixed(0)}€
              </span>
            </div>

            {/* Indicador de reseñas si las hay */}
            {provider.rating_count > 0 && (
              <div className="text-xs text-neutral-500">
                {provider.rating_count} reseña{provider.rating_count !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </div>
  );
}
